import React, { useEffect, useState } from "react";
import { Section } from "./Primitives";
import { api } from "../../lib/api";
import { formatLongDate } from "../../lib/format";
import { CheckCircle2, ExternalLink } from "lucide-react";

/**
 * Virtual "Follow-Ups Due" section for the Daily To-Do page.
 * Aggregates follow-ups from every ops collection + networking whose
 * follow_up_date == the currently viewed date, so anything Mike marked
 * "follow up on 6/12" shows up on the 6/12 Daily view even though the
 * source entry lives in another tab.
 */
export default function FollowUpsDueSection({ date, refresh, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/follow-ups");
        if (cancel) return;
        setItems(
          (data || []).filter(
            (f) =>
              f.follow_up_date === date &&
              !f.follow_up_completed
          )
        );
      } catch (e) {
        // silent
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [date, tick, refreshKey]);

  if (!loading && items.length === 0) return null;

  const KIND_TO_ROUTE = {
    daily: "/operations/daily",
    pipeline: "/operations/pipeline",
    calling: "/operations/calling",
    intros: "/operations/intros",
    schedule: "/operations/schedule",
    networking: "/networking",
  };

  const markComplete = async (item) => {
    try {
      await api.post(`/follow-ups/${item.kind}/${item.entry_id}/complete`);
      setTick((t) => t + 1);
      refresh && refresh();
    } catch (e) {
      // silent
    }
  };

  return (
    <Section
      title="Follow-Ups Due Today"
      color="#E5484D"
      count={items.length}
      data-testid="followups-due-section"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.08em] text-neutral-500 font-semibold bg-[var(--rw-surface-2)]">
            <th className="py-2 px-3">Source</th>
            <th className="py-2 px-3">Individual</th>
            <th className="py-2 px-3">Company</th>
            <th className="py-2 px-3">Follow-Up</th>
            <th className="py-2 px-3">Original Entry</th>
            <th className="py-2 px-3 w-px" />
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr
              key={`${it.kind}-${it.entry_id}`}
              className="border-t border-[var(--rw-border)] bg-[#fef1f4]/60 hover:bg-[#fef1f4]"
              data-testid={`fu-due-${it.entry_id}`}
            >
              <td className="py-2 px-3 text-xs font-semibold uppercase tracking-[0.06em] text-[#E5484D]">
                {it.source}
              </td>
              <td className="py-2 px-3">{it.individual || "—"}</td>
              <td className="py-2 px-3">{it.company || "—"}</td>
              <td className="py-2 px-3 text-xs">{it.follow_up || "—"}</td>
              <td className="py-2 px-3 text-xs text-neutral-500 font-mono-rw">
                {it.date ? formatLongDate(it.date) : "—"}
              </td>
              <td className="py-2 px-3 text-right whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => markComplete(it)}
                  title="Mark follow-up complete"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--rw-border)] rounded-[4px] bg-white hover:bg-[var(--rw-surface-2)] mr-1"
                  data-testid={`fu-complete-${it.entry_id}`}
                >
                  <CheckCircle2 size={12} /> Done
                </button>
                <a
                  href={KIND_TO_ROUTE[it.kind] || "/follow-ups"}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--rw-border)] rounded-[4px] bg-white hover:bg-[var(--rw-surface-2)]"
                  data-testid={`fu-open-${it.entry_id}`}
                >
                  <ExternalLink size={12} /> Open
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}
